import { useIsFocused } from "@-/com.npmjs.expo-router/react-navigation";
import {
  PortalContextBridgeContent,
  usePortalContextValues,
} from "@-/dev.improve.haniverse.components.ui.portal-context-bridge";
import {
  PRIMARY_CONTROLS_STRIP_EXPANSION_HIDDEN,
  PrimaryControlsStripExpansionContext,
  type PrimaryControlsStripExpansionEntry,
  type PrimaryControlsStripExpansionPublishHandle,
} from "@-/dev.improve.haniverse.contexts.primary-controls-strip.expansion";
import { useContext, useEffect, useRef, useState, type ReactNode } from "react";

/**
 * Place once in `PrimaryControlsStrip` where the expanded details strip should appear.
 * Renders the screen-published content when a focused screen has published any, or
 * `null` when no screen has published content. The host itself does not manage the
 * expand/collapse toggle — that remains in `PrimaryControlsStrip` (or wherever you wire it).
 */
export function PrimaryControlsStripExpansionHost() {
  const registry = useContext(PrimaryControlsStripExpansionContext);
  const [current, setCurrent] = useState<ReactNode>(() => registry.getCurrent());

  useEffect(() => {
    setCurrent(registry.getCurrent());
    return registry.subscribe(setCurrent);
  }, [registry]);

  return <>{current}</>;
}

export type PrimaryControlsStripExpansionProps =
  | { children: ReactNode; hidden?: false }
  | { children?: undefined; hidden: true };

/**
 * Renders custom content inside the expanded header strip from screen content by publishing
 * to the registry backing `PrimaryControlsStripExpansionProvider`. When multiple callsites are mounted
 * simultaneously, only the most recently registered one is rendered. Unmounting/blurring the
 * screen removes the entry without leaking state.
 *
 * Pass `hidden` (and no children) to suppress the strip — including content published by an
 * enclosing layout — for as long as this screen is focused. The expand/collapse toggle hides
 * too. Useful when a screen nested under a layout that publishes its own expansion content
 * wants a plain header (e.g. a slide lesson under the Learn layout).
 */
export function PrimaryControlsStripExpansion({
  children,
  hidden,
}: PrimaryControlsStripExpansionProps) {
  const isFocused = useIsFocused();
  const registry = useContext(PrimaryControlsStripExpansionContext);
  const publishHandleRef = useRef<PrimaryControlsStripExpansionPublishHandle | null>(null);
  const contextValues = usePortalContextValues();

  const component = (
    <PortalContextBridgeContent values={contextValues}>{children}</PortalContextBridgeContent>
  );

  const publishedNodeRef = useRef<PrimaryControlsStripExpansionEntry>(
    hidden ? PRIMARY_CONTROLS_STRIP_EXPANSION_HIDDEN : component,
  );
  publishedNodeRef.current = hidden ? PRIMARY_CONTROLS_STRIP_EXPANSION_HIDDEN : component;

  useEffect(() => {
    if (!isFocused) {
      publishHandleRef.current?.unpublish();
      publishHandleRef.current = null;
      return;
    }

    publishHandleRef.current = registry.publish(publishedNodeRef.current);

    return () => {
      publishHandleRef.current?.unpublish();
      publishHandleRef.current = null;
    };
  }, [isFocused, registry]);

  useEffect(() => {
    if (!isFocused || publishHandleRef.current == null) {
      return;
    }
    publishHandleRef.current.update(publishedNodeRef.current);
  }, [children, contextValues, hidden, isFocused, registry]);

  return null;
}
